class BlogFilter extends HTMLElement {
  connectedCallback() {
    this.sectionId   = this.dataset.sectionId;
    this.blogHandle  = this.dataset.blogHandle;
    this.currentTag  = this.dataset.currentTag || '';
    this.currentSort = 'date-descending';
    this._fetching   = false;

    this.grid        = this.querySelector('[data-blog-grid]');
    this.tabs        = Array.from(this.querySelectorAll('[data-blog-tab]'));
    this.sortWrapper = this.querySelector('[data-blog-sort]');

    if (!this.grid) return;

    this._bindTabs();
    this._bindPagination();
    if (this.sortWrapper) this._bindSort();
    this._initFromURL();

    this._onPopState = (e) => this._handlePopState(e);
    window.addEventListener('popstate', this._onPopState);

    this._onDocClick = (e) => { if (this.sortWrapper && !this.sortWrapper.contains(e.target)) this._closeSort(); };
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('click', this._onDocClick);
  }

  // ── Bindings ──────────────────────────────────────────────────

  _bindTabs() {
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this._onTabClick(tab));
      tab.addEventListener('keydown', (e) => this._onTabKeydown(e));
    });
  }

  _bindPagination() {
    this.grid.addEventListener('click', (e) => {
      const link = e.target.closest('a.pagination__item');
      if (!link) return;
      e.preventDefault();
      this._fetchGrid(link.href, true);
    });
  }

  _bindSort() {
    const trigger = this.sortWrapper.querySelector('[data-blog-sort-trigger]');
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = this.sortWrapper.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', String(isOpen));
      });
    }
    // Event delegation — catches clicks on any option regardless of bind timing
    this.sortWrapper.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-sort-value]');
      if (!opt) return;
      e.stopPropagation();
      this._setSortValue(opt.dataset.sortValue);
    });
    // Keyboard: Enter/Space on option
    this.sortWrapper.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const opt = e.target.closest('[data-sort-value]');
      if (!opt) return;
      e.preventDefault();
      this._setSortValue(opt.dataset.sortValue);
    });
  }

  _setSortValue(value) {
    this.currentSort = value;
    // Update trigger label
    const labelEl    = this.sortWrapper.querySelector('[data-blog-sort-label]');
    const chosenOpt  = this.sortWrapper.querySelector(`[data-sort-value="${value}"]`);
    if (labelEl && chosenOpt) labelEl.textContent = chosenOpt.textContent.trim();
    // Update option states
    this.sortWrapper.querySelectorAll('[data-sort-value]').forEach((opt) => {
      const active = opt.dataset.sortValue === value;
      opt.classList.toggle('is-selected', active);
      opt.setAttribute('aria-selected', String(active));
    });
    this._closeSort();
    this._sortArticles();
    const url = new URL(window.location.href);
    if (value === 'date-descending') { url.searchParams.delete('sort'); }
    else { url.searchParams.set('sort', value); }
    window.history.replaceState({ tag: this.currentTag, sort: value }, '', url.toString());
  }

  _closeSort() {
    if (!this.sortWrapper) return;
    this.sortWrapper.classList.remove('is-open');
    const trigger = this.sortWrapper.querySelector('[data-blog-sort-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  // ── URL init ──────────────────────────────────────────────────

  _initFromURL() {
    const sortParam = new URLSearchParams(window.location.search).get('sort');
    if (sortParam && this.sortWrapper) {
      this._setSortValue(sortParam);
    }
  }

  // ── Tab interaction ───────────────────────────────────────────

  _onTabClick(tab) {
    const tag = tab.dataset.tag;
    if (tag === this.currentTag && !this._fetching) return;
    this.currentTag = tag;
    this.tabs.forEach((t) => {
      const active = t === tab;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', String(active));
    });
    this._fetchGrid(this._buildTagURL(tag), true);
  }

  _onTabKeydown(e) {
    const idx = this.tabs.indexOf(e.currentTarget);
    if (idx === -1) return;
    let next = -1;
    if (e.key === 'ArrowRight') next = (idx + 1) % this.tabs.length;
    if (e.key === 'ArrowLeft')  next = (idx - 1 + this.tabs.length) % this.tabs.length;
    if (e.key === 'Home')       next = 0;
    if (e.key === 'End')        next = this.tabs.length - 1;
    if (next !== -1) { e.preventDefault(); this.tabs[next].focus(); this.tabs[next].click(); }
  }

  // ── URL helpers ───────────────────────────────────────────────

  _buildTagURL(tag) {
    if (!tag) return `/blogs/${this.blogHandle}`;
    const safeTag = encodeURIComponent(tag).replace(/%3A/gi, ':');
    return `/blogs/${this.blogHandle}/tagged/${safeTag}`;
  }

  _mergeSortIntoURL(href) {
    const url = new URL(href, window.location.origin);
    if (this.currentSort && this.currentSort !== 'date-descending') {
      url.searchParams.set('sort', this.currentSort);
    } else {
      url.searchParams.delete('sort');
    }
    return url;
  }

  // ── AJAX fetch ────────────────────────────────────────────────

  async _fetchGrid(href, pushState = false) {
    if (this._fetching) return;
    this._fetching = true;
    this._setLoading(true);

    const targetURL = this._mergeSortIntoURL(href);

    // Shopify Sections Rendering API returns JSON: { "section-id": "<html>" }
    const fetchURL = new URL(targetURL.toString());
    fetchURL.searchParams.set('sections', this.sectionId);

    try {
      const response = await fetch(fetchURL.toString());

      // 404 on a tagged URL = no articles with that tag — not a hard error
      if (!response.ok && response.status !== 404) {
        throw new Error('HTTP ' + response.status);
      }

      let newGrid = null;

      if (response.ok) {
        const data        = await response.json();
        // Shopify returns the HTML keyed directly by section id (flat)
        const sectionHTML = data[this.sectionId] || data.sections?.[this.sectionId];

        if (sectionHTML) {
          const parser = new DOMParser();
          const doc    = parser.parseFromString(sectionHTML, 'text/html');
          newGrid      = doc.querySelector('[data-blog-grid]');
        }
      }

      if (newGrid) {
        // Strip AOS from new nodes BEFORE injecting.
        // After AJAX the articles land already inside the viewport — AOS sets
        // opacity:0 + transform on them and the IntersectionObserver never
        // re-fires for elements that are already visible, leaving them hidden.
        newGrid.querySelectorAll('[data-aos]').forEach((el) => {
          el.removeAttribute('data-aos');
          el.removeAttribute('data-aos-delay');
          el.removeAttribute('data-aos-duration');
          el.classList.remove('aos-init', 'aos-animate');
        });

        this.grid.innerHTML = newGrid.innerHTML;

        if (this.currentSort !== 'date-descending') {
          this._sortArticles();
        }
      } else {
        // No matching articles — show empty state, no page reload
        const articlesGrid = this.grid.querySelector('[data-articles-grid]');
        if (articlesGrid) {
          articlesGrid.innerHTML = '<p class="main-blog__empty">No posts found for this category.</p>';
        }
        const paginationEl = this.grid.querySelector('[data-blog-pagination]');
        if (paginationEl) paginationEl.innerHTML = '';
      }

      // Update browser URL
      if (pushState) {
        const displayURL = new URL(targetURL.toString());
        window.history.pushState(
          { tag: this.currentTag, sort: this.currentSort },
          '',
          displayURL.pathname + (displayURL.search || '')
        );
      }

      this._syncTabsToTag(this.currentTag);
      this.grid.scrollIntoView({ behavior: 'smooth', block: 'start' });

    } catch (err) {
      console.error('[BlogFilter] AJAX fetch error:', err);
      this._syncTabsToTag(this.currentTag);
    } finally {
      this._setLoading(false);
      this._fetching = false;
    }
  }

  // ── Client-side sort ──────────────────────────────────────────

  _sortArticles() {
    const articlesGrid = this.grid.querySelector('[data-articles-grid]');
    if (!articlesGrid) return;

    const items = Array.from(articlesGrid.querySelectorAll('[data-article]'));
    if (!items.length) return;

    items.sort((a, b) => {
      switch (this.currentSort) {
        case 'date-ascending':
          return new Date(a.dataset.date) - new Date(b.dataset.date);
        case 'title-ascending':
          return (a.dataset.title || '').localeCompare(b.dataset.title || '');
        case 'title-descending':
          return (b.dataset.title || '').localeCompare(a.dataset.title || '');
        default:
          return new Date(b.dataset.date) - new Date(a.dataset.date);
      }
    });

    const frag = document.createDocumentFragment();
    items.forEach((item) => frag.appendChild(item));
    articlesGrid.appendChild(frag);
  }

  // ── Tab sync ──────────────────────────────────────────────────

  _syncTabsToTag(tag) {
    this.tabs.forEach((t) => {
      const active = t.dataset.tag === tag;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', String(active));
    });
  }

  // ── Loading state ─────────────────────────────────────────────

  _setLoading(loading) {
    this.grid.classList.toggle('is-loading', loading);
    this.grid.setAttribute('aria-busy', String(loading));
  }

  // ── Back / forward (popstate) ─────────────────────────────────

  _handlePopState(e) {
    const state      = e.state || {};
    const pathMatch  = window.location.pathname.match(/\/tagged\/([^/]+)/);

    this.currentTag  = state.tag !== undefined
      ? state.tag
      : (pathMatch ? decodeURIComponent(pathMatch[1]) : '');

    const sortParam  = new URLSearchParams(window.location.search).get('sort');
    const sortValue  = state.sort || sortParam || 'date-descending';
    this.currentSort = sortValue;
    if (this.sortWrapper) this._setSortValue(sortValue);

    this._fetchGrid(window.location.href, false);
  }
}

if (!customElements.get('blog-filter')) {
  customElements.define('blog-filter', BlogFilter);
}